// FieldIQ — construction "Sites" tab (site_lookup). The crew's job-site lookup:
// the facility's active job sites, searchable by name / client / address, each
// with what's happening on site now (current phase + sub) and one-tap directions.
// Reads the same crew-redacted BuildIQ state as the Work Log (no pricing). This
// is the construction counterpart to the political LookupTab — kept SEPARATE so
// political voter/precinct/district data never bleeds into a construction tenant.
import { useCallback, useEffect, useMemo, useState } from "react";
import { HardHat, Loader2, MapPin, Navigation, RefreshCw, Search, WifiOff } from "lucide-react";
import { biqGetState } from "../lib/buildiq";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const STATUS_LABEL = { won: "Scheduled", progress: "In progress" };

// What's happening on this site right now: the in-progress phase, else the one
// whose window covers today, else the next upcoming — whatever the crew heads to.
const currentPhase = (job) => {
  const phases = (job && job.schedule && job.schedule.phases) || [];
  const today = todayISO();
  const live = phases.filter((p) => p && p.status !== "done");
  return (
    live.find((p) => p.status === "in progress") ||
    live.find((p) => p.start && p.start <= today && (!p.end || p.end >= today)) ||
    live.filter((p) => p.start && p.start > today).sort((a, b) => (a.start < b.start ? -1 : 1))[0] ||
    null
  );
};

const fullAddr = (info) => [info && info.addr, info && info.cityzip].filter(Boolean).join(", ");
const mapsUrl = (info) => `https://maps.google.com/?q=${encodeURIComponent(fullAddr(info))}`;

export default function SitesTab({ facility, isOnline, onAskAbout }) {
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    biqGetState()
      .then((d) => (d && d.error ? setErr(d.error) : setDb(d)))
      .catch(() => setErr("network"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sites = useMemo(() => {
    if (!db) return [];
    const order = Array.isArray(db.order) ? db.order : [];
    const ids = [...order, ...Object.keys(db.jobs || {})].filter(
      (id, i, a) => db.jobs && db.jobs[id] && a.indexOf(id) === i,
    );
    let list = ids
      .map((id) => db.jobs[id])
      .filter((j) => j && ["won", "progress"].includes(j.status) && j.info && j.info.addr);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((j) =>
        `${j.name || ""} ${(j.info && j.info.name) || ""} ${fullAddr(j.info)}`.toLowerCase().includes(s),
      );
    }
    // in-progress first, then scheduled; stable within
    return list.sort((a, b) => (a.status === b.status ? 0 : a.status === "progress" ? -1 : 1));
  }, [db, q]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-48 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading sites…
      </div>
    );

  if (err)
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500">
        {err === "network" ? <WifiOff size={28} className="text-slate-700" /> : <MapPin size={28} className="text-slate-700" />}
        <p className="text-sm font-medium">Couldn’t load your sites.</p>
        <button type="button" onClick={load} className="flex items-center gap-2 text-sky-400 text-sm">
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    );

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-slate-400">
        <Search size={16} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find a site — name, client, or address"
          aria-label="Search sites"
          className="flex-1 bg-transparent outline-none text-slate-200 text-sm placeholder:text-slate-500"
        />
      </label>

      {!sites.length ? (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <HardHat size={32} className="text-slate-700" />
          <p className="text-slate-400 text-sm font-medium">{q ? "No sites match your search." : "No active job sites"}</p>
          {!q && <p className="text-slate-500 text-xs">Won and in-progress jobs with an address show up here.</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {sites.map((j) => {
            const ph = currentPhase(j);
            return (
              <div key={j.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-semibold truncate">{j.name || "Untitled Job"}</div>
                    {j.info && j.info.name && <div className="text-slate-400 text-xs mt-0.5 truncate">{j.info.name}</div>}
                  </div>
                  <span
                    className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                      j.status === "progress" ? "bg-amber-900/40 text-amber-300" : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    {STATUS_LABEL[j.status] || j.status}
                  </span>
                </div>

                <div className="flex items-start gap-1.5 mt-2 text-slate-300 text-sm">
                  <MapPin size={15} className="text-slate-500 mt-0.5 shrink-0" />
                  <span className="min-w-0">{fullAddr(j.info)}</span>
                </div>

                {ph && (
                  <div className="mt-2 text-xs text-slate-400">
                    On site now: <span className="text-slate-200 font-medium">{ph.trade || ph.label || "Phase"}</span>
                    {ph.sub ? <> · {ph.sub}</> : ""}
                  </div>
                )}

                <div className="flex gap-2 mt-3">
                  <a
                    href={mapsUrl(j.info)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sky-400 text-sm font-medium"
                  >
                    <Navigation size={14} /> Directions
                  </a>
                  {onAskAbout && (
                    <button
                      type="button"
                      onClick={() => onAskAbout(`the ${j.name || "job"} site at ${fullAddr(j.info)}`)}
                      className="text-slate-400 text-sm font-medium ml-auto"
                    >
                      Ask Bob
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isOnline && (
        <p className="text-slate-500 text-xs text-center pt-1">Offline — showing the last synced sites.</p>
      )}
    </div>
  );
}
